import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useProjects,
  useUpdateProject,
  useDeleteProject,
  useAddProjectMember,
  useRemoveProjectMember,
} from '@/hooks/useProjects';
import { useUsers } from '@/hooks/useUsers';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import {
  FolderKanban,
  Search,
  Users,
  GitBranch,
  CheckCircle,
  XCircle,
  Pencil,
  Trash2,
  X,
  ChevronRight,
  UserPlus,
  UserMinus,
  AlertTriangle,
  LayoutDashboard,
  RefreshCw,
  AlertCircle,
  ExternalLink,
} from 'lucide-react';
import { Project, User } from '@/types';
import { UpdateProjectData } from '@/api/projects.api';
import apiClient from '@/api/client';

/**
 * Project edit modal (for settings only, not creation)
 */
function ProjectEditModal({
  project,
  onClose,
  onSave,
  isLoading,
}: {
  project: Project;
  onClose: () => void;
  onSave: (data: UpdateProjectData) => void;
  isLoading: boolean;
}) {
  const [formData, setFormData] = useState({
    name: project.name || '',
    description: project.description || '',
    standupTime: project.settings?.standupTime || '09:00',
    standupTimezone: project.settings?.standupTimezone || 'UTC',
    inactivityThresholdDays: project.settings?.inactivityThresholdDays || 7,
    staleIssueDays: project.settings?.staleIssueDays || 5,
    autoCommentOnStale: project.settings?.autoCommentOnStale ?? true,
    notifyOnStale: project.settings?.notifyOnStale ?? true,
    requireIssueLinks: project.settings?.requireIssueLinks || false,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      name: formData.name,
      description: formData.description || undefined,
      settings: {
        standupTime: formData.standupTime,
        standupTimezone: formData.standupTimezone,
        inactivityThresholdDays: formData.inactivityThresholdDays,
        staleIssueDays: formData.staleIssueDays,
        autoCommentOnStale: formData.autoCommentOnStale,
        notifyOnStale: formData.notifyOnStale,
        requireIssueLinks: formData.requireIssueLinks,
      },
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-background rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">Edit Project Settings</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <Label htmlFor="name">Project Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="My Project"
            />
          </div>
          <div>
            <Label htmlFor="description">Description</Label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Project description..."
              className="w-full px-3 py-2 border rounded-md text-sm min-h-[80px] bg-background"
            />
          </div>

          <div className="border-t pt-4">
            <h3 className="text-sm font-medium mb-3">Stale Issue Settings</h3>
            <div>
              <Label htmlFor="staleIssueDays">Days until issue is stale</Label>
              <Input
                id="staleIssueDays"
                type="number"
                min={1}
                max={30}
                value={formData.staleIssueDays}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    staleIssueDays: parseInt(e.target.value) || 5,
                  })
                }
              />
              <p className="text-xs text-muted-foreground mt-1">
                Issues without updates for this many days will be flagged
              </p>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <input
                type="checkbox"
                id="autoCommentOnStale"
                checked={formData.autoCommentOnStale}
                onChange={(e) =>
                  setFormData({ ...formData, autoCommentOnStale: e.target.checked })
                }
                className="rounded"
              />
              <Label htmlFor="autoCommentOnStale">Auto-comment on stale issues in GitLab</Label>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <input
                type="checkbox"
                id="notifyOnStale"
                checked={formData.notifyOnStale}
                onChange={(e) =>
                  setFormData({ ...formData, notifyOnStale: e.target.checked })
                }
                className="rounded"
              />
              <Label htmlFor="notifyOnStale">Notify PM and executives on stale issues</Label>
            </div>
          </div>

          <div className="border-t pt-4">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="requireIssueLinks"
                checked={formData.requireIssueLinks}
                onChange={(e) =>
                  setFormData({ ...formData, requireIssueLinks: e.target.checked })
                }
                className="rounded"
              />
              <Label htmlFor="requireIssueLinks">Require issue links in commits</Label>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? <LoadingSpinner className="h-4 w-4" /> : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Delete confirmation modal
 */
function DeleteConfirmModal({
  project,
  onClose,
  onConfirm,
  isLoading,
}: {
  project: Project;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-background rounded-lg shadow-xl max-w-sm w-full">
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold flex items-center gap-2 text-red-600">
            <AlertTriangle className="h-5 w-5" />
            Delete Project
          </h2>
        </div>
        <div className="p-4">
          <p className="text-sm text-muted-foreground mb-4">
            Are you sure you want to delete <strong>{project.name}</strong>? This will remove the
            local project record but will not affect the GitLab project.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={onConfirm} disabled={isLoading}>
              {isLoading ? <LoadingSpinner className="h-4 w-4" /> : 'Delete'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Member management panel
 */
function MemberManagementPanel({
  project,
  onClose,
}: {
  project: Project;
  onClose: () => void;
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const { data: usersData } = useUsers({ search: searchQuery, limit: 20 });
  const addMemberMutation = useAddProjectMember();
  const removeMemberMutation = useRemoveProjectMember();

  const memberIds = Array.isArray(project.memberIds)
    ? project.memberIds.map((m) => (typeof m === 'object' ? m.id : m))
    : [];

  const members = Array.isArray(project.memberIds)
    ? project.memberIds.filter((m): m is User => typeof m === 'object')
    : [];

  const availableUsers = usersData?.data?.filter((u) => !memberIds.includes(u.id)) || [];

  const handleAddMember = async (userId: string) => {
    await addMemberMutation.mutateAsync({ projectId: project.id, userId });
  };

  const handleRemoveMember = async (userId: string) => {
    await removeMemberMutation.mutateAsync({ projectId: project.id, userId });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex justify-end">
      <div className="w-full max-w-md bg-background h-full flex flex-col shadow-xl">
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <h2 className="font-semibold">Manage Members</h2>
            <p className="text-sm text-muted-foreground">{project.name}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Current members */}
          <div>
            <h3 className="text-sm font-medium mb-2">
              Current Members ({members.length})
            </h3>
            {members.length === 0 ? (
              <p className="text-sm text-muted-foreground">No members assigned</p>
            ) : (
              <div className="space-y-2">
                {members.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center justify-between p-2 bg-muted rounded"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <Users className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <div className="text-sm font-medium">{member.displayName}</div>
                        <div className="text-xs text-muted-foreground">
                          @{member.username}
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveMember(member.id)}
                      disabled={removeMemberMutation.isPending}
                    >
                      <UserMinus className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add new members */}
          <div>
            <h3 className="text-sm font-medium mb-2">Add Members</h3>
            <Input
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="mb-2"
            />
            {availableUsers.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {searchQuery ? 'No users found' : 'All users are already members'}
              </p>
            ) : (
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {availableUsers.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between p-2 border rounded hover:bg-muted"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                        <Users className="h-4 w-4" />
                      </div>
                      <div>
                        <div className="text-sm font-medium">{user.displayName}</div>
                        <div className="text-xs text-muted-foreground">
                          @{user.username} - {user.role}
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleAddMember(user.id)}
                      disabled={addMemberMutation.isPending}
                    >
                      <UserPlus className="h-4 w-4 text-green-500" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Project card component
 */
function ProjectCard({
  project,
  onEdit,
  onDelete,
  onManageMembers,
  onNavigate,
}: {
  project: Project;
  onEdit: () => void;
  onDelete: () => void;
  onManageMembers: () => void;
  onNavigate: () => void;
}) {
  const memberCount = Array.isArray(project.memberIds) ? project.memberIds.length : 0;
  const repoCount = Array.isArray(project.repoIds) ? project.repoIds.length : 0;
  const isSyncedFromGitLab = project.syncedFromGitLab;
  const gitlabProject = project.gitlabProject as {
    webUrl?: string;
    pathWithNamespace?: string;
  } | undefined;

  return (
    <Card className="hover:border-primary/50 transition-colors group">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0 cursor-pointer" onClick={onNavigate}>
            <div className="flex items-center gap-2 mb-1">
              {isSyncedFromGitLab ? (
                <GitBranch className="h-5 w-5 text-orange-500 flex-shrink-0" />
              ) : (
                <FolderKanban className="h-5 w-5 text-primary flex-shrink-0" />
              )}
              <span className="font-medium truncate">{project.name}</span>
              <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{project.code}</code>
              {project.active ? (
                <span className="inline-flex items-center text-xs text-green-600">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Active
                </span>
              ) : (
                <span className="inline-flex items-center text-xs text-red-600">
                  <XCircle className="h-3 w-3 mr-1" />
                  Inactive
                </span>
              )}
            </div>

            {gitlabProject?.pathWithNamespace && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
                <GitBranch className="h-3 w-3" />
                <span>{gitlabProject.pathWithNamespace}</span>
                {gitlabProject.webUrl && (
                  <a
                    href={gitlabProject.webUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-primary"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            )}

            {project.description && (
              <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                {project.description}
              </p>
            )}

            <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Users className="h-3 w-3" />
                {memberCount} member{memberCount !== 1 ? 's' : ''}
              </span>
              <span className="flex items-center gap-1">
                <GitBranch className="h-3 w-3" />
                {repoCount} repo{repoCount !== 1 ? 's' : ''}
              </span>
              {project.managerId && typeof project.managerId === 'object' && (
                <span className="flex items-center gap-1">
                  PM: {project.managerId.displayName}
                </span>
              )}
              {project.settings?.staleIssueDays && (
                <span className="flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  Stale: {project.settings.staleIssueDays}d
                </span>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button
              variant="ghost"
              size="icon"
              onClick={onNavigate}
              title="View Dashboard"
            >
              <LayoutDashboard className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onManageMembers}
              title="Manage Members"
            >
              <Users className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={onEdit} title="Edit Settings">
              <Pencil className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={onDelete} title="Delete Project">
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Projects() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [deletingProject, setDeletingProject] = useState<Project | null>(null);
  const [managingMembers, setManagingMembers] = useState<Project | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{
    projects?: { created: number; updated: number; errors: number };
  } | null>(null);

  // Fetch projects
  const { data: projectsData, isLoading, refetch } = useProjects({
    page,
    limit: 20,
    search: searchQuery || undefined,
    sortBy: 'name',
    sortOrder: 'asc',
  });

  // Mutations
  const updateMutation = useUpdateProject();
  const deleteMutation = useDeleteProject();

  const handleUpdate = async (data: UpdateProjectData) => {
    if (!editingProject) return;
    await updateMutation.mutateAsync({ id: editingProject.id, data });
    setEditingProject(null);
  };

  const handleDelete = async () => {
    if (!deletingProject) return;
    await deleteMutation.mutateAsync(deletingProject.id);
    setDeletingProject(null);
  };

  const handleNavigateToDashboard = (projectId: string) => {
    navigate(`/projects/${projectId}`);
  };

  const handleSyncFromGitLab = async () => {
    setIsSyncing(true);
    setSyncResult(null);
    try {
      const response = await apiClient.post('/gitlab/sync/projects');
      setSyncResult({ projects: response.data.data });
      refetch();
    } catch (error) {
      console.error('Sync failed:', error);
    } finally {
      setIsSyncing(false);
    }
  };

  const syncedCount = projectsData?.data?.filter((p) => p.syncedFromGitLab).length || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Projects</h1>
          <p className="text-muted-foreground">
            Projects synced from GitLab with issue tracking
          </p>
        </div>
        <Button onClick={handleSyncFromGitLab} disabled={isSyncing}>
          <RefreshCw className={`h-4 w-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
          {isSyncing ? 'Syncing...' : 'Sync from GitLab'}
        </Button>
      </div>

      {/* Sync result */}
      {syncResult?.projects && (
        <Card className="bg-green-50 border-green-200">
          <CardContent className="py-3">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle className="h-4 w-4" />
              <span className="text-sm">
                Sync complete: {syncResult.projects.created} created,{' '}
                {syncResult.projects.updated} updated, {syncResult.projects.errors} errors
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Projects</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {projectsData?.pagination?.total || 0}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Synced from GitLab</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{syncedCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Active Projects</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {projectsData?.data?.filter((p) => p.active).length || 0}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Members</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {projectsData?.data?.reduce(
                (acc, p) => acc + (Array.isArray(p.memberIds) ? p.memberIds.length : 0),
                0
              ) || 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search projects..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(1);
          }}
          className="pl-10"
        />
      </div>

      {/* Project List */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : !projectsData?.data?.length ? (
        <Card>
          <CardContent className="py-12 text-center">
            <GitBranch className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No projects found</h3>
            <p className="text-muted-foreground mb-4">
              {searchQuery
                ? 'Try a different search term'
                : 'Click "Sync from GitLab" to import your projects'}
            </p>
            {!searchQuery && (
              <Button onClick={handleSyncFromGitLab} disabled={isSyncing}>
                <RefreshCw className={`h-4 w-4 mr-2 ${isSyncing ? 'animate-spin' : ''}`} />
                Sync from GitLab
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {projectsData.data.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              onEdit={() => setEditingProject(project)}
              onDelete={() => setDeletingProject(project)}
              onManageMembers={() => setManagingMembers(project)}
              onNavigate={() => handleNavigateToDashboard(project.id)}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {projectsData?.pagination?.totalPages && projectsData.pagination.totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </Button>
          <span className="flex items-center px-4 text-sm text-muted-foreground">
            Page {page} of {projectsData.pagination.totalPages}
          </span>
          <Button
            variant="outline"
            disabled={page >= projectsData.pagination.totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      )}

      {/* Edit Modal */}
      {editingProject && (
        <ProjectEditModal
          project={editingProject}
          onClose={() => setEditingProject(null)}
          onSave={handleUpdate}
          isLoading={updateMutation.isPending}
        />
      )}

      {/* Delete Confirmation */}
      {deletingProject && (
        <DeleteConfirmModal
          project={deletingProject}
          onClose={() => setDeletingProject(null)}
          onConfirm={handleDelete}
          isLoading={deleteMutation.isPending}
        />
      )}

      {/* Member Management */}
      {managingMembers && (
        <MemberManagementPanel
          project={managingMembers}
          onClose={() => setManagingMembers(null)}
        />
      )}
    </div>
  );
}
